// Photo-a-doubt → step-by-step solution. Snap/upload a problem photo (OCR'd
// server-side) or type it, and get a worked walkthrough. Uploads go straight to
// S3 via a presigned PUT; only the s3Key is sent to the API.
import { useState } from 'react';
import { apiPost } from '@/lib/api';
import { Button, Card, StudentPage, StudentPageHeader } from '@/components/ds';
import { MathText } from '@/components/exam/MathText';
import { fontPx } from '@/lib/fontSize';

interface DoubtResult {
  problemText: string;
  steps: string[];
  finalAnswer: string;
  confidence: number;
  manualReview?: boolean;
}

export function DoubtPage() {
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DoubtResult | null>(null);

  async function uploadPhoto(f: File): Promise<string> {
    const { uploadUrl, s3Key } = await apiPost<{ uploadUrl: string; s3Key: string }>(
      '/uploads/doubt-presign',
      {
        contentType: f.type,
      },
    );
    const put = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': f.type },
      body: f,
    });
    if (!put.ok) throw new Error('Upload failed');
    return s3Key;
  }

  async function solve() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      let s3Key: string | undefined;
      if (file) s3Key = await uploadPhoto(file);
      const payload = s3Key ? { s3Key } : { text: text.trim() };
      const r = await apiPost<DoubtResult>('/doubt', payload);
      setResult(r);
    } catch {
      setError('Couldn’t solve that just now. Please try again, or rephrase / retake the photo.');
    } finally {
      setBusy(false);
    }
  }

  const canSolve = (Boolean(file) || text.trim().length > 3) && !busy;

  return (
    <StudentPage>
      <StudentPageHeader
        title="Ask a doubt"
        subtitle="Stuck on a problem? Snap a photo or type it, and get a clear step-by-step solution."
      />

      <Card>
        <div className="flex flex-col gap-4 p-1">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type your question…  e.g. Solve 2x² − 5x − 3 = 0"
            rows={3}
            maxLength={2000}
            data-control=""
            style={{
              width: '100%',
              padding: '0.5rem var(--control-px-md)',
              fontSize: 'var(--control-text-md)',
              color: 'var(--text-body)',
              background: 'var(--surface-card)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--control-radius)',
              outline: 'none',
              lineHeight: 1.5,
              resize: 'vertical',
              transition: 'border-color var(--dur-fast) var(--ease-standard)',
            }}
          />

          <div className="flex flex-wrap items-center gap-3">
            <label className="cursor-pointer rounded-card border border-line px-4 py-2 text-sm font-semibold text-forest">
              {file ? `📷 ${file.name.slice(0, 28)}` : '📷 Upload a photo'}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
            {file && (
              <button className="text-xs text-ink-soft underline" onClick={() => setFile(null)}>
                remove
              </button>
            )}
            <span className="text-xs text-ink-faint">A photo takes priority over typed text.</span>
          </div>

          <div>
            <Button
              style={{ padding: '11px 20px', fontSize: fontPx(15) }}
              disabled={!canSolve}
              onClick={solve}
            >
              {busy ? 'Solving…' : 'Solve it →'}
            </Button>
          </div>

          {error && <p className="text-sm text-danger">{error}</p>}
        </div>
      </Card>

      {result && (
        <Card>
          <div className="flex flex-col gap-3 p-1">
            {result.problemText && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
                  Problem
                </div>
                <div className="text-ink">
                  <MathText>{result.problemText}</MathText>
                </div>
              </div>
            )}

            {result.steps.length > 0 && (
              <div>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-faint">
                  Solution
                </div>
                <ol className="flex flex-col gap-2">
                  {result.steps.map((s, i) => (
                    <li key={i} className="flex gap-2 text-ink">
                      <span className="font-bold text-forest" data-numeric="">
                        {i + 1}.
                      </span>
                      <span>
                        <MathText>{s}</MathText>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {result.finalAnswer && (
              <div
                className="rounded-card p-3"
                style={{ background: 'var(--green-bg)', color: 'var(--green-text)' }}
              >
                <span className="font-semibold">Answer: </span>
                <MathText>{result.finalAnswer}</MathText>
              </div>
            )}

            {result.manualReview && (
              <p className="text-xs text-ink-soft">
                I wasn’t fully confident here — double-check with your textbook, or rephrase the
                question.
              </p>
            )}
          </div>
        </Card>
      )}
    </StudentPage>
  );
}
